import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import IC_COLLECTOR_CLOSE from "@/assets/common/ic_collect_close.svg?react";
import { VoiceCollectStep } from "./const";
import VoiceCollectForm, {
  VoiceCollectFormInfo,
  VoiceCollectorInfo,
} from "./VoiceCollectForm";
import VoiceCollector from "./VoiceCollector";
import VoiceCollectResult from "./VoiceCollectResult";
import VoiceCollectFail from "./VoiceCollectFail";
import ReactDOM from "react-dom";
import VoiceModalBg from "@/assets/common/voice_modal_bg.png";
import { addVoice } from "@/services/voice";
import { uploadFile } from "@/services/upload";
import { tryToGetPermission } from "@/utils/audioStream";
import AudioPermissionDenied from "./AudioPermissionDenied";
import { defaultAvatarPrefix } from "@/utils/newAvatar";
import fridayReporter from "@/utils/friday";
import { message } from "antd";

interface VoiceAddModalProps {
  handleCallback: () => void;
}

export interface VoiceAddModalRef {
  open: () => void;
  close: () => void;
}

const VoiceAddModal = forwardRef<VoiceAddModalRef, VoiceAddModalProps>(
  (props, ref) => {
    const { handleCallback } = props;
    const [formInfo, setFormInfo] = useState<VoiceCollectFormInfo>();
    const [voiceInfo, setVoiceInfo] = useState<VoiceCollectorInfo>();
    const [step, setStep] = useState(VoiceCollectStep.form);
    const [visible, setVisible] = useState(false);
    const [addVoiceErrMsg, setAddVoiceErrMsg] = useState("");
    const [isSubmitting, setSubmitting] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
      if (visible) {
        setStep(VoiceCollectStep.form);
      }
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [visible]);

    const onClose = () => {
      setSubmitting(false);
      setVisible(false);
    };

    const handleCollectVoice = async (info: VoiceCollectFormInfo) => {
      setFormInfo(info);
      // 检查是否有权限
      const permission = await tryToGetPermission();
      if (!permission) {
        setStep(VoiceCollectStep.permissionDenied);
        return;
      }
      setStep(VoiceCollectStep.collectVoice);
    };

    const handleCollectResult = (wavBlob: Blob, copyWriting: string) => {
      const url = URL.createObjectURL(wavBlob);
      console.log("tmpAudioUrl", url);
      setVoiceInfo({
        url,
        blob: wavBlob,
        copyWriting,
      });
      setStep(VoiceCollectStep.result);
    };

    const handleReCollect = () => {
      handleCancel();
      setStep(VoiceCollectStep.collectVoice);
    };

    const uploadWavFile = async (signal: AbortSignal) => {
      if (!voiceInfo?.blob) return;

      const formData = new FormData();
      formData.append("file", voiceInfo?.blob, "recording.wav");

      const result = await uploadFile(formData, signal);
      return result;
    };

    const handleCollectFail = () => {
      setStep(VoiceCollectStep.fail);
      fridayReporter.pushData({
        eventCode: 'HomePage_VoiceFiles_Collect_CollectStatus',
        payload: {
          collect_status: 'fail'
        }
      })
    }

    const handleSubmit = async () => {
      try {
        if (!formInfo || !voiceInfo || isSubmitting) return;
        setSubmitting(true);
        // 创建一个新的 AbortController 实例
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        const file = await uploadWavFile(signal);
        if (!file) {
          handleCollectFail();
          return;
        }

        await addVoice(
          {
            roleName: formInfo.speakerName,
            avatar: `${defaultAvatarPrefix}${formInfo.avatarIndex}`,
            audioFileKey: file.fileKey,
            copyWriting: voiceInfo.copyWriting,
          },
          signal
        );
        setSubmitting(false);
        setVisible(false);
        message.success(`“${formInfo?.speakerName}”声纹采集成功`);
        fridayReporter.pushData({
          eventCode: 'HomePage_VoiceFiles_Collect_CollectStatus',
          payload: {
            collect_status: 'success'
          }
        })
        handleCallback();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error("声纹采集失败", error);
        if (error.errCode === "ERR_CANCELED") {
          console.log("请求已取消");
        } else {
          handleCollectFail();
          setAddVoiceErrMsg("声纹采集失败");
          setSubmitting(false);
        }
      }
    };

    const handleCancel = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setSubmitting(false); // 确保在取消请求后更新状态
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        setVisible(true);
      },
      close: onClose,
    }));

    return visible
      ? ReactDOM.createPortal(
        <div
          className={`fixed w-full h-full inset-0 flex items-center justify-center z-999 bg-black bg-opacity-50`}
        >
          <div className="bg-white w-130 h-95 rounded-4 shadow relative overflow-hidden">
            <img
              src={VoiceModalBg}
              className="absolute w-full h-25 left--0.5px top--0.5px"
            />
            <div
              className="absolute right-3 top-3 cursor-pointer"
              onClick={onClose}
            >
              <IC_COLLECTOR_CLOSE className="w-6 h-6" />
            </div>
            {step === VoiceCollectStep.form && (
              <VoiceCollectForm handleNextStep={handleCollectVoice} />
            )}
            {step === VoiceCollectStep.collectVoice && (
              <VoiceCollector handleNextStep={handleCollectResult} />
            )}
            {step === VoiceCollectStep.result && voiceInfo && (
              <VoiceCollectResult
                audioUrl={voiceInfo.url}
                handleReCollect={handleReCollect}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
            {step === VoiceCollectStep.fail && (
              <VoiceCollectFail
                speakerName={formInfo?.speakerName}
                handleReCollect={handleReCollect}
                errMsg={addVoiceErrMsg}
              />
            )}
            {step === VoiceCollectStep.permissionDenied && (
              <AudioPermissionDenied />
            )}
          </div>
        </div>,
        document.body
      )
      : null;
  }
);

export default VoiceAddModal;
